import React from 'react';

interface SeoHeadProps {
  title: string;
  description?: string;
  canonicalUrl?: string;
  indexable?: boolean;
  socialImageUrl?: string;
  robotsMaxSnippet?: number;
  ogType?: string;
}

export const SeoHead: React.FC<SeoHeadProps> = ({
  title,
  description,
  canonicalUrl,
  indexable = false,
  socialImageUrl,
  robotsMaxSnippet,
  ogType = 'website'
}) => {
  React.useEffect(() => {
    document.title = title;
    const ensure = (name: string, attr: 'name' | 'property', content: string) => {
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    ensure('robots', 'name', indexable ? 'index,follow' : 'noindex,nofollow');
    if (indexable && robotsMaxSnippet && robotsMaxSnippet > 0) {
      ensure('googlebot', 'name', `index,follow,max-snippet:${robotsMaxSnippet}`);
    }
    if (description) {
      ensure('description', 'name', description);
      ensure('og:description', 'property', description);
      ensure('twitter:description', 'name', description);
    }
    ensure('og:title', 'property', title);
    ensure('twitter:title', 'name', title);
    ensure('og:type', 'property', ogType);
    ensure('twitter:card', 'name', 'summary_large_image');
    const defaultImage = socialImageUrl || `${window.location.origin}/og/default.png`;
    ensure('og:image', 'property', defaultImage);
    ensure('twitter:image', 'name', defaultImage);
    if (canonicalUrl) {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', canonicalUrl);
      ensure('og:url', 'property', canonicalUrl);
    }
  }, [title, description, canonicalUrl, indexable, socialImageUrl, robotsMaxSnippet, ogType]);

  return null;
};

export default SeoHead;


